import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import type { NavigationScreenSpaceChangeRequestState } from '@gitbook/app-navigation';
import { useHiveClient } from '@gitbook/hive-react';
import { toast } from '@gitbook/spine-react';
import { Trace } from '@gitbook/util-debug';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { useNavigation } from '../../navigation';
import { apiClient } from '../../state';

/**
 * Return a function to create a change request.
 */
export function useCreateChangeRequest() {
    const hiveClient = useHiveClient();
    const { navigate } = useNavigation();

    return useEventCallback(
        async ({
            space,
            source,
            navigateTo,
            onBeforeNavigate,
        }: {
            space: api.Space;
            source: analytics.ChangeRequestCreateSource;

            /**
             * Screens to navigate to after the change request is created.
             */
            navigateTo?: Partial<NavigationScreenSpaceChangeRequestState>;

            /**
             * Callback to run before navigating to the change request.
             */
            onBeforeNavigate?: (input: { changeRequest: api.ChangeRequest }) => Promise<void>;
        }) => {
            const trace = new Trace('changeRequest:create');
            trace.label('Space', space.id);

            await trace.wrap(async () => {
                await hiveClient.sync.withReadOnlyDataSyncs(
                    async () => {
                        await toast.catch(async () => {
                            analytics.logEvent(
                                analytics.createSpaceEvent(space, {
                                    type: 'change_request_start_create',
                                    source,
                                })
                            );

                            const { data: newChangeRequest } =
                                await apiClient.changeRequests.createChangeRequest(space.id, {
                                    subject: '',
                                });

                            await onBeforeNavigate?.({
                                changeRequest: newChangeRequest,
                            });

                            navigate({
                                screen: {
                                    type: 'space',
                                    space: space.id,
                                    changeRequest: newChangeRequest.number,
                                    revision: undefined,
                                    sideSheet: undefined,
                                    mode: 'edit',
                                    ...navigateTo,
                                },
                            });

                            toast.success('Change request created', {
                                description: 'You can now edit the content in a change request',
                            });
                        }, 'Error while creating a new change request');
                    },
                    (sync) => sync.contentId === space.id
                );
            });
        }
    );
}
